/**
 * Procedural texture generation. Owned by `look`.
 *
 * EVERY texture in the game is drawn here, on a canvas, from the seeded RNG and the seeded
 * ValueNoise2D in `src/core/rng.js`. No image files (ARCHITECTURE.md N1) and no
 * `Math.random` (N4). Same seed → bit-identical texels → bit-identical shots (§7.3).
 *
 * Generation strategy: per-texel ImageData writes for anything noise-based (fully engine
 * independent), plus integer-aligned canvas-2D rectangles for structure (brick courses,
 * cone bands, window mullions). We never draw text, arcs with sub-pixel centres, or
 * gradients whose rasterisation could drift between Skia builds.
 *
 * Colour space: these are ALBEDO maps, so they are authored in sRGB and tagged
 * `SRGBColorSpace`. Masks / roughness / alpha maps are tagged `NoColorSpace`.
 */

import * as THREE from 'three';
import { ValueNoise2D } from '../core/rng.js';
import { MAT_TUNING as T } from './tuning.js';
import {
  ROAD, PATCH, MARKINGS, SIDEWALK, KERB, BRICK, STONE, RENDER_STUCCO, WINDOW, ROOF, TOWER,
  METAL, CONE, BARREL, SNOW, SLUSH, WATER, FLORA, SIGN, TRAFFIC, CHAR, TRUCK, FX,
  css, rgb255, mixHex, shadeHex,
} from './palette.js';

/* ══════════════════════════════════════════════════════════════════════════════════════
   canvas helpers
   ══════════════════════════════════════════════════════════════════════════════════════ */

function makeCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

function ctx2d(canvas) {
  return canvas.getContext('2d', { willReadFrequently: true, alpha: true, colorSpace: 'srgb' });
}

/** Wrap a canvas into a THREE.Texture with our standard settings. */
function toTexture(canvas, { srgb = true, repeat = 1, aniso = T.tex.anisotropy, name = '' } = {}) {
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.anisotropy = aniso;
  tex.generateMipmaps = T.tex.generateMipmaps;
  tex.minFilter = T.tex.generateMipmaps ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  tex.name = name;
  return tex;
}

const _SRGB_TO_LIN = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const v = i / 255;
    t[i] = v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }
  return t;
})();

/**
 * Alpha-weighted mean of a canvas in LINEAR sRGB, 0..1 per channel.
 *
 * Strided so a 512² tile costs ~4k samples rather than 262k — the value is a normalisation
 * constant, not a measurement, and the stride is fixed so it stays deterministic (§7.3).
 * Never returns 0 on a channel: the result is a divisor.
 */
function canvasMeanLinear(canvas) {
  const w = canvas.width | 0, h = canvas.height | 0;
  if (!w || !h) return { r: 1, g: 1, b: 1 };
  const g = ctx2d(canvas);
  const stride = Math.max(1, Math.floor(Math.min(w, h) / 64));
  let r = 0, gg = 0, b = 0, wsum = 0;
  // ONE ROW AT A TIME. `getImageData(0,0,w,h)` on every tile allocated ~10 MB of transient
  // ImageData during init and the profiler charged all of it to heap growth. A single 512-px
  // row is 2 KB, and we only read every `stride`-th row anyway.
  for (let y = 0; y < h; y += stride) {
    const d = g.getImageData(0, y, w, 1).data;
    for (let x = 0; x < w; x += stride) {
      const i = x * 4;
      const a = d[i + 3] / 255;
      if (a <= 0.004) continue;
      r += _SRGB_TO_LIN[d[i]] * a;
      gg += _SRGB_TO_LIN[d[i + 1]] * a;
      b += _SRGB_TO_LIN[d[i + 2]] * a;
      wsum += a;
    }
  }
  if (wsum < 1e-6) return { r: 1, g: 1, b: 1 };
  const k = 1 / wsum;
  return {
    r: Math.max(1e-3, r * k),
    g: Math.max(1e-3, gg * k),
    b: Math.max(1e-3, b * k),
  };
}

/**
 * Maps that carry DETAIL, not colour.
 *
 * Every one of these tiles was generated from a palette hex, and its material then multiplied
 * by that same hex — squaring the albedo. #A0A0A1 asphalt shaded as if it were #5F5F5F, the
 * truck chassis as #0B0B0C, a staircase as #101820. That single bug is most of why round 3's
 * frame median luminance came in at 102 against the reference's 165.
 *
 * The fix has to leave `material.color` equal to the palette anchor, because that IS the
 * published contract — `gameplay` reads `materials.get('road.curb').color` to derive its
 * excavation-floor colour, and `world` and `vehicle` rely on `get(key,{tint})` meaning "make
 * the final albedo this". So the normalisation happens HERE, in the tile: divide out the
 * tile's own mean so the map averages white and carries only grain, courses, cracks and
 * seams. The map then modulates the colour instead of doubling it.
 *
 * A map used by a key whose material colour is 0xFFFFFF must NOT be in this set — for those
 * the tile IS the albedo (cone stripes, barrel stripes, the window grid, the truck livery).
 *
 * WHY ONLY FOUR TILES. Normalising a tile to a mean of exactly white necessarily compresses
 * everything above the mean — half the texels — so it costs real detail (asphalt loses some
 * of its aggregate speckle). Every other key gets the same correction for free and losslessly
 * via `albedo: true`, which divides the mean out of `material.color` instead (colour may
 * legally exceed 1 in the linear working space; an 8-bit tile may not).
 *
 * These four are the exception because `gameplay` reads them as COLOURS, not as materials:
 * `readColor(mats, 'road.curb')` in src/gameplay/potholes.js derives the excavation floor and
 * wall from `material.color`, so for these four keys `material.color` has to stay equal to
 * the palette anchor. Losing a little speckle on the road is a cheap price for the hero
 * mechanic's crater being the right value.
 */
const NORMALISED_MAPS = new Set(['asphalt', 'kerb', 'patch.fresh', 'patch.cured']);

/**
 * Rescale a canvas in place so its alpha-weighted linear mean is white, preserving relative
 * detail. Values that would exceed 1 after the divide are soft-knee compressed rather than
 * clipped, so aggregate speckle and mortar highlights keep their ordering instead of
 * flattening into a plateau. Deterministic: no RNG, fixed traversal (§7.3).
 */
function normaliseCanvasToWhite(canvas) {
  const w = canvas.width | 0, h = canvas.height | 0;
  if (!w || !h) return;
  const mean = canvasMeanLinear(canvas);
  const g = ctx2d(canvas);
  const img = g.getImageData(0, 0, w, h);
  const d = img.data;
  const inv = [1 / mean.r, 1 / mean.g, 1 / mean.b];
  const KNEE = 0.78;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    for (let c = 0; c < 3; c++) {
      let n = _SRGB_TO_LIN[d[i + c]] * inv[c];
      if (n > KNEE) {
        // Asymptotic soft knee: strictly increasing, never reaches 1.0 + headroom.
        n = KNEE + (1 - KNEE) * (1 - Math.exp(-(n - KNEE) / (1 - KNEE)));
      }
      d[i + c] = Math.round(255 * (n <= 0.0031308 ? n * 12.92 : 1.055 * Math.pow(n, 1 / 2.4) - 0.055));
    }
  }
  g.putImageData(img, 0, 0);
}

/** Tileable fbm — samples noise on a torus so the tile has no seam. */
function tileFbm(noise, u, v, freq, octaves = 4) {
  // Sample four corners of the tile and blend — cheap seamless trick that keeps determinism.
  const a = noise.fbm(u * freq, v * freq, octaves);
  const b = noise.fbm((u - 1) * freq, v * freq, octaves);
  const c = noise.fbm(u * freq, (v - 1) * freq, octaves);
  const d = noise.fbm((u - 1) * freq, (v - 1) * freq, octaves);
  const wu = u, wv = v;
  const top = a * (1 - wu) + b * wu;
  const bot = c * (1 - wu) + d * wu;
  return top * (1 - wv) + bot * wv;
}

/* ══════════════════════════════════════════════════════════════════════════════════════
   TextureFactory
   ══════════════════════════════════════════════════════════════════════════════════════ */

export class TextureFactory {
  /**
   * @param {import('../core/rng.js').Rng} rng  a FORK, e.g. ctx.rng.fork('look.textures')
   */
  constructor(rng, maxAnisotropy = 8) {
    this.rng = rng;
    this.aniso = Math.min(T.tex.anisotropy, maxAnisotropy || 1);
    this.noise = new ValueNoise2D(rng.u32());
    this.noiseB = new ValueNoise2D(rng.u32());
    this._made = new Map();
    this.bytes = 0;
  }

  _register(name, tex) {
    tex.name = name;
    this._made.set(name, tex);
    const img = tex.image;
    if (img) this.bytes += img.width * img.height * 4 * 1.34; // +mip chain
    // DETAIL MAPS ARE NORMALISED TO A MEAN OF WHITE. See NORMALISED_MAPS.
    if (img && NORMALISED_MAPS.has(name)) normaliseCanvasToWhite(img);
    // Mean albedo of the tile, in LINEAR space. The material library divides by this when a
    // key declares `albedo: true` — see MaterialLibrary._applyToOne. For a normalised map it
    // is ~1 and the division is a no-op; it exists so an un-normalised map can still opt in
    // without silently squaring its albedo.
    if (img) tex.userData.meanLinear = canvasMeanLinear(img);
    tex.needsUpdate = true;
    return tex;
  }

  keys() { return Array.from(this._made.keys()); }
  count() { return this._made.size; }
  megabytes() { return +(this.bytes / 1048576).toFixed(2); }

  dispose() {
    for (const t of this._made.values()) t.dispose();
    this._made.clear();
  }

  /* ────────────────────────────────────────────────────────────── ASPHALT ─────────── */

  /**
   * The single most important texture in the game. A player at 90 km/h reads the road as a
   * bright ribbon; the texture's job is to give it grain and history WITHOUT dropping its
   * value. Aggregate speckle + large tonal blotching + hairline cracks + tar crack-seal
   * seams + a quilt of old patches.
   */
  asphalt({ size = T.tex.sizeLarge, base = ROAD.asphaltLit, quilt = true } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const n = this.noise, nb = this.noiseB;
    const rng = this.rng;

    const baseR = (base >> 16) & 255, baseG = (base >> 8) & 255, baseB = base & 255;
    const aggLight = rgb255(ROAD.aggregate);
    const aggDark = rgb255(ROAD.aggregateDark);

    for (let y = 0; y < size; y++) {
      const v = y / size;
      for (let x = 0; x < size; x++) {
        const u = x / size;
        // Large tonal drift: a real road is never one flat value.
        const blotch = (tileFbm(n, u, v, T.tex.asphaltBlotchScale, 3) - 0.5) * 2 * T.tex.asphaltBlotchAmount;
        // Fine grain.
        const grain = (tileFbm(nb, u, v, T.tex.asphaltNoiseScale, T.tex.asphaltNoiseOctaves) - 0.5) * 0.18;
        let k = 1 + blotch + grain;

        // Hairline cracks from ridged noise.
        const ridge = nb.ridged(u * T.tex.asphaltCrackScale + 31.7, v * T.tex.asphaltCrackScale + 11.3, 3);
        if (ridge > T.tex.asphaltCrackThreshold) {
          const t = (ridge - T.tex.asphaltCrackThreshold) / (1 - T.tex.asphaltCrackThreshold);
          k *= 1 - t * T.tex.asphaltCrackDarkness;
        }

        let r = baseR * k, gg = baseG * k, b = baseB * k;

        // Aggregate speckle — individual stones catching the light.
        const speck = n.sample(x * 1.913 + 5.5, y * 1.913 + 9.25);
        if (speck > 1 - T.tex.asphaltSpeckleDensity * 0.5) {
          const s = T.tex.asphaltSpeckleContrast;
          r += (aggLight.r - r) * s; gg += (aggLight.g - gg) * s; b += (aggLight.b - b) * s;
        } else if (speck < T.tex.asphaltSpeckleDensity * 0.5) {
          const s = T.tex.asphaltSpeckleContrast * 0.8;
          r += (aggDark.r - r) * s; gg += (aggDark.g - gg) * s; b += (aggDark.b - b) * s;
        }

        const i = (y * size + x) * 4;
        d[i] = r < 0 ? 0 : r > 255 ? 255 : r;
        d[i + 1] = gg < 0 ? 0 : gg > 255 ? 255 : gg;
        d[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);

    if (quilt) {
      // Old patch quilt — rectangles of a different asphalt generation, edges torn.
      for (let i = 0; i < T.tex.asphaltPatchCount; i++) {
        const w = Math.round(size * rng.range(T.tex.asphaltPatchMin, T.tex.asphaltPatchMax));
        const h = Math.round(size * rng.range(T.tex.asphaltPatchMin, T.tex.asphaltPatchMax));
        const px = rng.int(0, size), py = rng.int(0, size);
        const dark = rng.range(0.6, 1.0) * T.tex.asphaltPatchDarken;
        g.fillStyle = css(PATCH.season1, dark);
        // Torn edge: draw as a stack of jittered bands rather than one clean rect.
        const bands = 6;
        for (let bIdx = 0; bIdx < bands; bIdx++) {
          const by = py + Math.round((h / bands) * bIdx);
          const jx = Math.round(rng.spread(4));
          const jw = Math.round(rng.spread(6));
          g.fillRect(px + jx, by, w + jw, Math.ceil(h / bands) + 1);
          g.fillRect(px + jx - size, by, w + jw, Math.ceil(h / bands) + 1); // wrap
          g.fillRect(px + jx, by - size, w + jw, Math.ceil(h / bands) + 1);
        }
      }

      // Tar crack-seal seams — the glossy black snakes every Montreal street has.
      g.strokeStyle = css(ROAD.tarSeam, 0.85);
      g.lineWidth = T.tex.asphaltSeamWidth;
      g.lineCap = 'round';
      for (let i = 0; i < T.tex.asphaltSeamCount; i++) {
        const horizontal = rng.chance(0.5);
        const at = rng.int(0, size);
        g.beginPath();
        const steps = 10;
        for (let s = 0; s <= steps; s++) {
          const t = (s / steps) * size;
          const off = Math.round(Math.sin(s * 1.7 + i * 2.3) * T.tex.asphaltSeamWobble + rng.spread(3));
          const X = horizontal ? t : at + off;
          const Y = horizontal ? at + off : t;
          if (s === 0) g.moveTo(X, Y); else g.lineTo(X, Y);
        }
        g.stroke();
      }

      // Oil drip staining down the centre of the lane.
      g.fillStyle = css(ROAD.oilStain, 0.10);
      for (let i = 0; i < 5; i++) {
        const px = rng.int(0, size), py = rng.int(0, size);
        const w = rng.int(6, 26), h = rng.int(10, 40);
        g.fillRect(px, py, w, h);
      }
    }

    return this._register('asphalt', toTexture(c, { aniso: this.aniso, name: 'asphalt' }));
  }

  /** Fresh hot-mix patch: near-black, fine even grain, a slight sheen mottle. */
  patchFresh({ size = T.tex.sizeMed } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const n = this.noiseB;
    const base = rgb255(PATCH.freshDry);
    const hi = rgb255(PATCH.freshSheen);
    const lo = rgb255(PATCH.freshWet);

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const grain = tileFbm(n, u, v, 14.0, 3);
        const mottle = tileFbm(this.noise, u, v, 3.0, 2);
        let r = base.r, gg = base.g, b = base.b;
        const t = (grain - 0.5) * 2 * T.tex.patchGrainContrast;
        if (t > 0) { r += (hi.r - r) * t; gg += (hi.g - gg) * t; b += (hi.b - b) * t; }
        else { r += (lo.r - r) * -t; gg += (lo.g - gg) * -t; b += (lo.b - b) * -t; }
        const m = 1 + (mottle - 0.5) * 0.16;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, r * m);
        d[i + 1] = Math.min(255, gg * m);
        d[i + 2] = Math.min(255, b * m);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register('patch.fresh', toTexture(c, { aniso: this.aniso, name: 'patch.fresh' }));
  }

  /** Cured patch: mid grey, oxidised, aggregate showing through. */
  patchCured({ size = T.tex.sizeMed } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const base = rgb255(PATCH.cured);
    const hi = rgb255(ROAD.aggregate);
    const lo = rgb255(PATCH.season1);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const grain = tileFbm(this.noise, u, v, 11.0, 3);
        const speck = this.noiseB.sample(x * 1.71 + 3.1, y * 1.71 + 7.9);
        let r = base.r, gg = base.g, b = base.b;
        const t = (grain - 0.5) * 2 * 0.22;
        if (t > 0) { r += (hi.r - r) * t * 0.8; gg += (hi.g - gg) * t * 0.8; b += (hi.b - b) * t * 0.8; }
        else { r += (lo.r - r) * -t; gg += (lo.g - gg) * -t; b += (lo.b - b) * -t; }
        if (speck > 0.86) { r += (hi.r - r) * 0.35; gg += (hi.g - gg) * 0.35; b += (hi.b - b) * 0.35; }
        const i = (y * size + x) * 4;
        d[i] = r; d[i + 1] = gg; d[i + 2] = b; d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register('patch.cured', toTexture(c, { aniso: this.aniso, name: 'patch.cured' }));
  }

  /* ────────────────────────────────────────────────────────────── CONCRETE ────────── */

  concrete({ size = T.tex.sizeMed, base = SIDEWALK.concrete, joints = true, name = 'concrete' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rgbBase = rgb255(base);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const grain = (tileFbm(this.noiseB, u, v, T.tex.concreteNoiseScale, 4) - 0.5) * 2 * T.tex.concreteGrain;
        const blotch = (tileFbm(this.noise, u, v, 2.4, 3) - 0.5) * 2 * 0.07;
        const k = 1 + grain + blotch;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, rgbBase.r * k);
        d[i + 1] = Math.min(255, rgbBase.g * k);
        d[i + 2] = Math.min(255, rgbBase.b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);

    if (joints) {
      g.fillStyle = css(SIDEWALK.joint, 0.55);
      for (let p = 0; p < size; p += T.tex.concreteJointEvery) {
        g.fillRect(p, 0, T.tex.concreteJointWidth, size);
        g.fillRect(0, p, size, T.tex.concreteJointWidth);
      }
      // Stains and repairs.
      g.fillStyle = css(SIDEWALK.concreteShade, 0.10);
      for (let i = 0; i < T.tex.concreteStainCount; i++) {
        const px = this.rng.int(0, size), py = this.rng.int(0, size);
        g.fillRect(px, py, this.rng.int(8, 40), this.rng.int(8, 40));
      }
    }
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /* ────────────────────────────────────────────────────────────── MASONRY ─────────── */

  /**
   * Running-bond brick. `lit`/`mid`/`shade` come straight from the sampled Montreal photos.
   * Per-brick tonal jitter is what stops a facade reading as wallpaper.
   */
  brick({ size = T.tex.sizeLarge, lit = BRICK.redLit, mid = BRICK.redMid, shade = BRICK.redShade, name = 'brick' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const rng = this.rng;

    // Mortar bed first.
    g.fillStyle = css(BRICK.mortar);
    g.fillRect(0, 0, size, size);
    // Mortar grain.
    {
      const img = g.getImageData(0, 0, size, size);
      const d = img.data;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const k = 1 + (this.noiseB.sample(x * 0.9, y * 0.9) - 0.5) * 0.12;
          const i = (y * size + x) * 4;
          d[i] *= k; d[i + 1] *= k; d[i + 2] *= k;
        }
      }
      g.putImageData(img, 0, 0);
    }

    const ch = T.tex.brickCourseH, bl = T.tex.brickLen, mo = T.tex.brickMortar;
    const rows = Math.ceil(size / ch);
    for (let row = 0; row < rows; row++) {
      const y = row * ch;
      const offset = (row % 2) * (bl / 2);
      for (let bx = -bl; bx < size + bl; bx += bl) {
        const x = Math.round(bx + offset);
        const j = rng.range(-T.tex.brickToneJitter, T.tex.brickToneJitter);
        let colHex = j > 0 ? mixHex(mid, lit, j / T.tex.brickToneJitter)
          : mixHex(mid, shade, -j / T.tex.brickToneJitter * 0.6);
        if (rng.chance(0.05)) colHex = mixHex(colHex, shade, 0.55);   // the odd burnt header
        g.fillStyle = css(colHex);
        g.fillRect(x, y, bl - mo, ch - mo);
      }
    }

    // Overall grain + a soot gradient toward the bottom (street grime).
    {
      const img = g.getImageData(0, 0, size, size);
      const d = img.data;
      for (let y = 0; y < size; y++) {
        const soot = 1 - Math.pow(y / size, 3) * 0.10;
        for (let x = 0; x < size; x++) {
          const k = (1 + (this.noise.sample(x * 1.3 + 4.4, y * 1.3 + 8.8) - 0.5) * 2 * T.tex.brickGrain) * soot;
          const i = (y * size + x) * 4;
          d[i] *= k; d[i + 1] *= k; d[i + 2] *= k;
        }
      }
      g.putImageData(img, 0, 0);
    }

    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Coursed rubble greystone — irregular block lengths, warm neutral limestone. */
  stone({ size = T.tex.sizeLarge, name = 'stone' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const rng = this.rng;

    g.fillStyle = css(STONE.greystoneShade);
    g.fillRect(0, 0, size, size);

    const ch = T.tex.stoneCourseH, jo = T.tex.stoneJoint;
    const rows = Math.ceil(size / ch);
    for (let row = 0; row < rows; row++) {
      const y = row * ch;
      let x = -rng.int(0, T.tex.stoneLenMax);
      while (x < size + T.tex.stoneLenMax) {
        const w = rng.int(T.tex.stoneLenMin, T.tex.stoneLenMax);
        const j = rng.range(-T.tex.stoneToneJitter, T.tex.stoneToneJitter);
        const base = j > 0 ? mixHex(STONE.greystoneMidLit, STONE.greystoneLit, j / T.tex.stoneToneJitter)
          : mixHex(STONE.greystoneMidLit, STONE.greystoneMid, -j / T.tex.stoneToneJitter);
        g.fillStyle = css(base);
        g.fillRect(Math.round(x), y, w - jo, ch - jo);
        // Tooled face: a light top edge and a dark bottom edge sells the depth cheaply.
        g.fillStyle = css(STONE.greystoneLit, 0.35);
        g.fillRect(Math.round(x), y, w - jo, 2);
        g.fillStyle = css(STONE.greystoneDeep, 0.40);
        g.fillRect(Math.round(x), y + ch - jo - 2, w - jo, 2);
        x += w;
      }
    }

    {
      const img = g.getImageData(0, 0, size, size);
      const d = img.data;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const k = 1 + (this.noiseB.sample(x * 1.6 + 2.2, y * 1.6 + 6.1) - 0.5) * 2 * T.tex.stoneGrain;
          const i = (y * size + x) * 4;
          d[i] *= k; d[i + 1] *= k; d[i + 2] *= k;
        }
      }
      g.putImageData(img, 0, 0);
    }
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Flat painted render / stucco. */
  renderCoat({ size = T.tex.sizeMed, base = RENDER_STUCCO.pale, name = 'render' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rgbBase = rgb255(base);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const grain = (tileFbm(this.noiseB, u, v, T.tex.renderNoiseScale, 3) - 0.5) * 2 * T.tex.renderGrain;
        const drift = (tileFbm(this.noise, u, v, 2.0, 2) - 0.5) * 2 * 0.05;
        const k = 1 + grain + drift;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, rgbBase.r * k);
        d[i + 1] = Math.min(255, rgbBase.g * k);
        d[i + 2] = Math.min(255, rgbBase.b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Flat tar-and-gravel roof. */
  roof({ size = T.tex.sizeMed } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const base = rgb255(ROOF.tar), grav = rgb255(ROOF.gravel);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const speck = this.noise.sample(x * 2.3 + 1.1, y * 2.3 + 4.7);
        const t = speck > 0.62 ? (speck - 0.62) / 0.38 * 0.7 : 0;
        const i = (y * size + x) * 4;
        d[i] = base.r + (grav.r - base.r) * t;
        d[i + 1] = base.g + (grav.g - base.g) * t;
        d[i + 2] = base.b + (grav.b - base.b) * t;
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register('roof', toTexture(c, { aniso: this.aniso, name: 'roof' }));
  }

  /* ────────────────────────────────────────────────────────────── WINDOWS ─────────── */

  /**
   * A window grid. Returns { map, emissive } — the emissive map lights a deterministic
   * subset of panes so night facades read as inhabited without any extra geometry.
   */
  windowGrid({ size = T.tex.sizeMed } = {}) {
    const cols = T.tex.windowCols, rows = T.tex.windowRows;
    const mull = T.tex.windowMullion;
    const cw = size / cols, chh = size / rows;
    const rng = this.rng;

    const cMap = makeCanvas(size, size);
    const gMap = ctx2d(cMap);
    const cEm = makeCanvas(size, size);
    const gEm = ctx2d(cEm);

    gMap.fillStyle = css(WINDOW.mullion);
    gMap.fillRect(0, 0, size, size);
    gEm.fillStyle = '#000000';
    gEm.fillRect(0, 0, size, size);

    for (let r = 0; r < rows; r++) {
      for (let cIdx = 0; cIdx < cols; cIdx++) {
        const x = Math.round(cIdx * cw) + mull;
        const y = Math.round(r * chh) + mull;
        const w = Math.round(cw) - mull * 2;
        const h = Math.round(chh) - mull * 2;

        // Daytime pane: dark reflective slab with a sky-reflection wedge in the top corner.
        const dayTone = mixHex(WINDOW.glassDay, WINDOW.glassDayLit, rng.range(0, 0.55));
        gMap.fillStyle = css(dayTone);
        gMap.fillRect(x, y, w, h);
        gMap.fillStyle = css(WINDOW.glassDayLit, 0.5);
        gMap.fillRect(x, y, w, Math.max(2, Math.round(h * rng.range(0.10, 0.34))));

        // Night: some panes lit.
        if (rng.chance(T.tex.windowLitFraction)) {
          // 0.78 -> 0.66. Reference §9.2 asks for a warm zone and a cool zone in one frame,
          // and lit windows are the only cool source that covers real facade area. See
          // palette.js WINDOW.litCool for the measurement that forced both changes.
          const warm = rng.chance(0.66);
          gEm.fillStyle = css(warm ? WINDOW.litWarm : WINDOW.litCool, rng.range(0.55, 1.0));
          gEm.fillRect(x, y, w, h);
          // A blind pulled partway down — cheap, but it stops the grid reading as a checkerboard.
          if (rng.chance(0.4)) {
            gEm.fillStyle = 'rgba(0,0,0,0.65)';
            gEm.fillRect(x, y, w, Math.round(h * rng.range(0.2, 0.6)));
          }
        }
      }
    }

    const map = this._register('window.map', toTexture(cMap, { aniso: this.aniso, name: 'window.map' }));
    const emissive = this._register('window.emissive', toTexture(cEm, { aniso: this.aniso, name: 'window.emissive' }));
    return { map, emissive };
  }

  /**
   * CURTAIN WALL — the downtown tower glazing. Returns { map, emissive }, same shape as
   * `windowGrid`, so `building.tower.glass` lands in the existing EMISSIVE family and costs
   * no new shader program.
   *
   * Three things make this read as a tower rather than a big house:
   *
   *  1. The mullion lattice is ANISOTROPIC — vertical mullions are thicker than the
   *     horizontal transoms (T.tex.towerMullionV/H). A curtain wall's verticals run
   *     uninterrupted floor to floor and that is the line the eye picks up at distance.
   *  2. Panes are blue-green (TOWER.glassDay), and each gets a reflection wedge whose SIZE
   *     varies per pane, so the face has vertical banding rather than a uniform sheen.
   *  3. Lit panes clump BY FLOOR at night. An office tower lights up in bands; an evenly
   *     random grid reads as static once the tower is 200 m away, which is where it will
   *     spend most of its screen life.
   *
   * Deterministic (§7.3): seeded rng + integer-aligned rects only.
   */
  curtainWall({ size = T.tex.sizeMed } = {}) {
    const cols = T.tex.towerCols, rows = T.tex.towerRows;
    const mv = T.tex.towerMullionV, mh = T.tex.towerMullionH;
    const cw = size / cols, chh = size / rows;
    const rng = this.rng;

    const cMap = makeCanvas(size, size);
    const gMap = ctx2d(cMap);
    const cEm = makeCanvas(size, size);
    const gEm = ctx2d(cEm);

    // The lattice is the background; panes are punched into it.
    gMap.fillStyle = css(TOWER.mullion);
    gMap.fillRect(0, 0, size, size);
    gEm.fillStyle = '#000000';
    gEm.fillRect(0, 0, size, size);

    // Per-floor lit bias, drawn ONCE per row so a whole floor tends to agree with itself.
    const floorLit = new Float32Array(rows);
    for (let r = 0; r < rows; r++) floorLit[r] = rng.next();

    for (let r = 0; r < rows; r++) {
      // Vertical tonal drift down the tile: upper panes catch more sky.
      const skyward = rows > 1 ? 1 - r / (rows - 1) : 1;
      for (let cIdx = 0; cIdx < cols; cIdx++) {
        const x = Math.round(cIdx * cw) + mv;
        const y = Math.round(r * chh) + mh;
        const w = Math.round(cw) - mv * 2;
        const h = Math.round(chh) - mh * 2;
        if (w <= 0 || h <= 0) continue;

        // Pane body: blue-green, biased toward the lit tone higher up the tile.
        const deep = mixHex(TOWER.glassDeep, TOWER.glassDay, rng.range(0.35, 1.0));
        gMap.fillStyle = css(mixHex(deep, TOWER.glassDayLit, skyward * rng.range(0.05, 0.30)));
        gMap.fillRect(x, y, w, h);

        // Sky-reflection wedge across the top of the pane. Varying its depth per pane is
        // what breaks up the grid without needing a second texture.
        gMap.fillStyle = css(TOWER.glassDayLit, rng.range(0.20, 0.55));
        gMap.fillRect(x, y, w, Math.max(1, Math.round(h * rng.range(0.12, 0.42))));

        // Night: floor bias blended with an independent per-pane draw.
        const bias = T.tex.towerLitFloorBias;
        const p = floorLit[r] * bias + rng.next() * (1 - bias);
        if (p < T.tex.towerLitFraction) {
          const warm = rng.chance(T.tex.towerLitWarmChance);
          gEm.fillStyle = css(warm ? TOWER.litOfficeWarm : TOWER.litOffice, rng.range(0.60, 1.0));
          gEm.fillRect(x, y, w, h);
          // A dark slab across part of the pane — ceiling void, partitions, a dark corner
          // office. Same job as the residential blind: kill the checkerboard.
          if (rng.chance(0.35)) {
            gEm.fillStyle = 'rgba(0,0,0,0.55)';
            gEm.fillRect(x, y + Math.round(h * rng.range(0.45, 0.75)), w, Math.round(h * 0.3));
          }
        }
      }
    }

    const map = this._register('tower.glass.map', toTexture(cMap, { aniso: this.aniso, name: 'tower.glass.map' }));
    const emissive = this._register('tower.glass.emissive', toTexture(cEm, { aniso: this.aniso, name: 'tower.glass.emissive' }));
    return { map, emissive };
  }

  /* ────────────────────────────────────────────────────────────── CONE ────────────── */

  /**
   * Cone body: orange with two retroreflective white bands. UV convention is v=0 at the
   * BASE, v=1 at the TIP — matching a THREE.ConeGeometry. The bands are what make a cone
   * findable at 90 km/h; orange alone is only 1.9:1 against lit asphalt.
   */
  coneStripes({ size = T.tex.sizeSmall, dirty = 0 } = {}) {
    const w = size, h = size * 2;
    const c = makeCanvas(w, h);
    const g = ctx2d(c);
    const rng = this.rng;

    // Body with a vertical shade ramp (deep at the base, rim-lit at the tip).
    const img = g.createImageData(w, h);
    const d = img.data;
    for (let y = 0; y < h; y++) {
      const v = 1 - y / h;      // canvas y=0 is the TOP of the texture = tip
      let bodyHex;
      if (v > 0.72) bodyHex = mixHex(CONE.lit, CONE.litRim, (v - 0.72) / 0.28);
      else if (v > 0.38) bodyHex = mixHex(CONE.mid, CONE.lit, (v - 0.38) / 0.34);
      else if (v > 0.14) bodyHex = mixHex(CONE.shade, CONE.mid, (v - 0.14) / 0.24);
      else bodyHex = mixHex(CONE.deepShade, CONE.shade, v / 0.14);
      if (dirty > 0) bodyHex = mixHex(bodyHex, CONE.dirty, dirty);
      const rgbB = rgb255(bodyHex);
      for (let x = 0; x < w; x++) {
        const k = 1 + (this.noiseB.sample(x * 2.7, y * 2.7) - 0.5) * 2 * 0.05;
        const i = (y * w + x) * 4;
        d[i] = Math.min(255, rgbB.r * k);
        d[i + 1] = Math.min(255, rgbB.g * k);
        d[i + 2] = Math.min(255, rgbB.b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);

    // The two bands.
    const bands = [T.tex.coneBandTop, T.tex.coneBandBottom];
    for (const bv of bands) {
      const y0 = Math.round((1 - bv - T.tex.coneBandWidth * 0.5) * h);
      const bh = Math.max(2, Math.round(T.tex.coneBandWidth * h));
      const grad = g.createLinearGradient(0, y0, 0, y0 + bh);
      grad.addColorStop(0, css(CONE.bandShade));
      grad.addColorStop(0.35, css(CONE.bandLit));
      grad.addColorStop(1, css(CONE.bandShade));
      g.fillStyle = grad;
      g.fillRect(0, y0, w, bh);
    }

    // Scuffs — a cone that has lived on a Montreal street has been hit.
    g.fillStyle = css(CONE.deepShade, T.tex.coneScuff);
    for (let i = 0; i < 8; i++) {
      g.fillRect(rng.int(0, w), rng.int(0, h), rng.int(2, 9), rng.int(2, 7));
    }
    if (dirty > 0) {
      g.fillStyle = css(ROAD.asphaltShade, 0.28 * dirty);
      g.fillRect(0, Math.round(h * 0.78), w, Math.round(h * 0.22));
    }

    const name = dirty > 0 ? 'cone.knocked' : 'cone';
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Roadwork barrel: orange body with two white bands, horizontally banded. */
  barrelStripes({ size = T.tex.sizeSmall } = {}) {
    const w = size, h = size;
    const c = makeCanvas(w, h);
    const g = ctx2d(c);
    g.fillStyle = css(BARREL.orange);
    g.fillRect(0, 0, w, h);
    g.fillStyle = css(BARREL.white);
    g.fillRect(0, Math.round(h * 0.18), w, Math.round(h * 0.13));
    g.fillRect(0, Math.round(h * 0.56), w, Math.round(h * 0.13));
    // Ribbing.
    g.fillStyle = 'rgba(0,0,0,0.16)';
    for (let y = 0; y < h; y += 8) g.fillRect(0, y, w, 2);
    g.fillStyle = css(ROAD.asphaltShade, 0.25);
    g.fillRect(0, Math.round(h * 0.86), w, Math.round(h * 0.14));
    return this._register('barrel', toTexture(c, { aniso: this.aniso, name: 'barrel' }));
  }

  /* ────────────────────────────────────────────────────────────── WEATHER ─────────── */

  snow({ size = T.tex.sizeMed } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const lit = rgb255(SNOW.lit), bright = rgb255(SNOW.bright), sh = rgb255(SNOW.shadow);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        // Wind-drift ripples plus a fine crystalline sparkle.
        const drift = tileFbm(this.noise, u, v, T.tex.snowNoiseScale, 4);
        const fine = this.noiseB.sample(x * 3.1, y * 3.1);
        let r, gg, b;
        if (drift > 0.5) {
          const t = (drift - 0.5) * 2;
          r = lit.r + (bright.r - lit.r) * t; gg = lit.g + (bright.g - lit.g) * t; b = lit.b + (bright.b - lit.b) * t;
        } else {
          const t = (0.5 - drift) * 2;
          r = lit.r + (sh.r - lit.r) * t * 0.7; gg = lit.g + (sh.g - lit.g) * t * 0.7; b = lit.b + (sh.b - lit.b) * t * 0.7;
        }
        const k = 1 + (fine - 0.5) * 2 * T.tex.snowGrain;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, r * k);
        d[i + 1] = Math.min(255, gg * k);
        d[i + 2] = Math.min(255, b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register('snow', toTexture(c, { aniso: this.aniso, name: 'snow' }));
  }

  /** Slush: grey-blue mush with dark wheel ruts cut through to the asphalt. */
  slush({ size = T.tex.sizeMed } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rng = this.rng;
    const tracked = rgb255(SLUSH.tracked), wet = rgb255(SLUSH.wet), edge = rgb255(SLUSH.edge);

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const n = tileFbm(this.noise, u, v, 6.5, 4);
        const fine = tileFbm(this.noiseB, u, v, 22.0, 2);
        let r, gg, b;
        if (n > 0.52) { const t = (n - 0.52) / 0.48; r = tracked.r + (wet.r - tracked.r) * 0; gg = tracked.g; b = tracked.b; r = tracked.r * (1 + t * 0.06); gg = tracked.g * (1 + t * 0.06); b = tracked.b * (1 + t * 0.06); }
        else { const t = (0.52 - n) / 0.52; r = tracked.r + (edge.r - tracked.r) * t; gg = tracked.g + (edge.g - tracked.g) * t; b = tracked.b + (edge.b - tracked.b) * t; }
        const k = 1 + (fine - 0.5) * 2 * T.tex.slushGrain;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, r * k);
        d[i + 1] = Math.min(255, gg * k);
        d[i + 2] = Math.min(255, b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);

    // Wheel ruts — vertical dark bands where tyres have cut down to wet asphalt.
    for (let i = 0; i < T.tex.slushRutCount; i++) {
      const x = rng.int(0, size);
      const w = Math.max(3, Math.round(size * T.tex.slushRutWidth * rng.range(0.6, 1.4)));
      const grad = g.createLinearGradient(x, 0, x + w, 0);
      grad.addColorStop(0, css(SLUSH.rut, 0));
      grad.addColorStop(0.5, css(SLUSH.rut, 0.72));
      grad.addColorStop(1, css(SLUSH.rut, 0));
      g.fillStyle = grad;
      g.fillRect(x, 0, w, size);
      g.fillRect(x - size, 0, w, size);
    }
    // Road dirt thrown up onto the slush.
    g.fillStyle = css(SNOW.dirty, T.tex.slushDirt);
    for (let i = 0; i < 14; i++) g.fillRect(rng.int(0, size), rng.int(0, size), rng.int(4, 22), rng.int(3, 14));

    return this._register('slush', toTexture(c, { aniso: this.aniso, name: 'slush' }));
  }

  /* ────────────────────────────────────────────────────────────── SPRITES ─────────── */

  /** Radial soft particle. Alpha only — colour comes from the material / instance colour. */
  softSprite({ size = T.tex.sizeSprite, falloff = T.tex.softFalloff, name = 'sprite.soft' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const half = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x + 0.5 - half) / half, dy = (y + 0.5 - half) / half;
        const r = Math.sqrt(dx * dx + dy * dy);
        const a = r >= 1 ? 0 : Math.pow(1 - r, falloff);
        const i = (y * size + x) * 4;
        d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
        d[i + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const tex = toTexture(c, { srgb: true, aniso: 1, name });
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    return this._register(name, tex);
  }

  /** Hard-edged grain particle (asphalt chips, debris). */
  hardSprite({ size = T.tex.sizeSprite } = {}) {
    return this.softSprite({ size, falloff: T.tex.hardFalloff, name: 'sprite.hard' });
  }

  /** Vertical rain streak. Points sprites are screen-aligned, so a vertical streak stays vertical. */
  rainStreak({ size = T.tex.sizeSprite } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const halfW = size * T.tex.rainStreakAspect * 0.5;
    const cx = size / 2;
    for (let y = 0; y < size; y++) {
      const v = y / (size - 1);
      // Taper the head and tail so the streak has no hard cap.
      const taper = Math.sin(v * Math.PI);
      for (let x = 0; x < size; x++) {
        const dx = Math.abs(x + 0.5 - cx) / Math.max(1, halfW);
        const a = dx >= 1 ? 0 : Math.pow(1 - dx, 1.4) * Math.pow(taper, 0.6);
        const i = (y * size + x) * 4;
        d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
        d[i + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const tex = toTexture(c, { srgb: true, aniso: 1, name: 'sprite.rain' });
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    return this._register('sprite.rain', tex);
  }

  /** Six-armed snowflake blob — soft enough to read as snow, structured enough not to be dust. */
  snowFlake({ size = T.tex.sizeSprite } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const half = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x + 0.5 - half) / half, dy = (y + 0.5 - half) / half;
        const r = Math.sqrt(dx * dx + dy * dy);
        const ang = Math.atan2(dy, dx);
        const arms = 0.72 + 0.28 * Math.cos(ang * 6);
        const rr = r / arms;
        const a = rr >= 1 ? 0 : Math.pow(1 - rr, 1.7);
        const i = (y * size + x) * 4;
        d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
        d[i + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const tex = toTexture(c, { srgb: true, aniso: 1, name: 'sprite.snow' });
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    return this._register('sprite.snow', tex);
  }

  /** Splash ring — an expanding annulus for rain hitting the road. */
  splashRing({ size = T.tex.sizeSprite } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const half = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x + 0.5 - half) / half, dy = (y + 0.5 - half) / half;
        const r = Math.sqrt(dx * dx + dy * dy);
        const band = Math.exp(-Math.pow((r - 0.74) / 0.16, 2));
        const i = (y * size + x) * 4;
        d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
        d[i + 3] = Math.round(Math.min(1, band) * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const tex = toTexture(c, { srgb: true, aniso: 1, name: 'sprite.splash' });
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    return this._register('sprite.splash', tex);
  }

  /** Long soft streak for trails / spray cones. */
  trailSprite({ size = T.tex.sizeSprite } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    for (let y = 0; y < size; y++) {
      const v = y / (size - 1);
      const head = Math.pow(1 - v, 1.3);
      for (let x = 0; x < size; x++) {
        const u = (x + 0.5) / size;
        const across = 1 - Math.abs(u - 0.5) * 2;
        const a = Math.max(0, Math.pow(across, 1.6)) * head;
        const i = (y * size + x) * 4;
        d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
        d[i + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const tex = toTexture(c, { srgb: true, aniso: 1, name: 'sprite.trail' });
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    return this._register('sprite.trail', tex);
  }

  /* ────────────────────────────────────────────────────────────── MISC ───────────── */

  /** Chain-link fence — an alpha lattice. Every Montreal ruelle has one. */
  chainLink({ size = T.tex.sizeSmall } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    g.clearRect(0, 0, size, size);
    g.strokeStyle = css(METAL.galvanised);
    g.lineWidth = 2.5;
    const step = size / 6;
    g.beginPath();
    for (let i = -6; i <= 12; i++) {
      g.moveTo(i * step, 0); g.lineTo(i * step + size, size);
      g.moveTo(i * step, size); g.lineTo(i * step + size, 0);
    }
    g.stroke();
    const tex = toTexture(c, { aniso: this.aniso, name: 'fence' });
    return this._register('fence', tex);
  }

  /** Foliage card — a clump of leaves with an alpha silhouette. */
  foliage({ size = T.tex.sizeSmall } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const lit = rgb255(FLORA.leafLit), mid = rgb255(FLORA.leafMid), sh = rgb255(FLORA.leafShade);
    const half = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x + 0.5 - half) / half, dy = (y + 0.5 - half) / half;
        const r = Math.sqrt(dx * dx + dy * dy);
        const lump = this.noise.fbm(x * 0.09, y * 0.09, 3);
        const edge = r + (lump - 0.5) * 0.55;
        const a = edge > 0.95 ? 0 : 1;
        const shade = this.noiseB.fbm(x * 0.05 + 3, y * 0.05 + 7, 3);
        let r8, g8, b8;
        if (shade > 0.55) { const t = (shade - 0.55) / 0.45; r8 = mid.r + (lit.r - mid.r) * t; g8 = mid.g + (lit.g - mid.g) * t; b8 = mid.b + (lit.b - mid.b) * t; }
        else { const t = (0.55 - shade) / 0.55; r8 = mid.r + (sh.r - mid.r) * t; g8 = mid.g + (sh.g - mid.g) * t; b8 = mid.b + (sh.b - mid.b) * t; }
        const i = (y * size + x) * 4;
        d[i] = r8; d[i + 1] = g8; d[i + 2] = b8; d[i + 3] = a * 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register('foliage', toTexture(c, { aniso: this.aniso, name: 'foliage' }));
  }

  /** A flat 4×4 tinted tile. Used so untextured materials still share the `map` shader path. */
  flat(hex, name) {
    const size = 4;
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    g.fillStyle = css(hex);
    g.fillRect(0, 0, size, size);
    const tex = toTexture(c, { aniso: 1, name });
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    return this._register(name, tex);
  }

  /** Brushed metal — faint linear grain, for the exterior staircases and railings. */
  brushedMetal({ size = T.tex.sizeSmall, base = METAL.staircaseBlack, name = 'metal' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rgbBase = rgb255(base);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const k = 1 + (this.noiseB.sample(x * 0.6, y * 6.0) - 0.5) * 2 * 0.13
          + (this.noise.sample(x * 0.12, y * 0.12) - 0.5) * 2 * 0.07;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, rgbBase.r * k);
        d[i + 1] = Math.min(255, rgbBase.g * k);
        d[i + 2] = Math.min(255, rgbBase.b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Painted-metal panel with a faint orange-peel, for signs and truck bodywork. */
  paintedPanel({ size = T.tex.sizeSmall, base = TRUCK.yellow, name = 'paint' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rgbBase = rgb255(base);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const k = 1 + (tileFbm(this.noiseB, u, v, 20.0, 2) - 0.5) * 2 * 0.035;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, rgbBase.r * k);
        d[i + 1] = Math.min(255, rgbBase.g * k);
        d[i + 2] = Math.min(255, rgbBase.b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Road-marking stripe with worn edges. Alpha so it can sit on top of asphalt. */
  markingStripe({ size = T.tex.sizeSmall, base = MARKINGS.white } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rgbBase = rgb255(base);
    const worn = rgb255(MARKINGS.whiteWorn);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const wear = tileFbm(this.noise, u, v, 8.0, 3);
        const scuff = tileFbm(this.noiseB, u, v, 26.0, 2);
        const t = Math.min(1, Math.max(0, (wear - 0.30) / 0.5));
        const a = Math.min(1, Math.max(0, (wear - 0.16) / 0.35)) * (0.72 + scuff * 0.28);
        const i = (y * size + x) * 4;
        d[i] = worn.r + (rgbBase.r - worn.r) * t;
        d[i + 1] = worn.g + (rgbBase.g - worn.g) * t;
        d[i + 2] = worn.b + (rgbBase.b - worn.b) * t;
        d[i + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    return this._register('marking', toTexture(c, { aniso: this.aniso, name: 'marking' }));
  }

  /** Generic backlit sign panel face — a flat colour with a subtle vignette from the box edge. */
  signPanel({ size = T.tex.sizeSmall, base = SIGN.panelWhite, name = 'sign.panel' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    g.fillStyle = css(base);
    g.fillRect(0, 0, size, size);
    g.fillStyle = css(SIGN.frame, 0.55);
    const b = Math.max(2, Math.round(size * 0.05));
    g.fillRect(0, 0, size, b); g.fillRect(0, size - b, size, b);
    g.fillRect(0, 0, b, size); g.fillRect(size - b, 0, b, size);
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Cloth / skin base — a very faint weave so characters are not plastic. */
  cloth({ size = T.tex.sizeSmall, base = CHAR.cloth[0], name = 'cloth' } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rgbBase = rgb255(base);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const weave = ((x >> 1) + (y >> 1)) % 2 === 0 ? 1.03 : 0.97;
        const k = weave * (1 + (this.noise.sample(x * 0.7, y * 0.7) - 0.5) * 0.06);
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, rgbBase.r * k);
        d[i + 1] = Math.min(255, rgbBase.g * k);
        d[i + 2] = Math.min(255, rgbBase.b * k);
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return this._register(name, toTexture(c, { aniso: this.aniso, name }));
  }

  /** Water surface — a subtle ripple normal-ish albedo for puddles and wet overlay. */
  wetSheen({ size = T.tex.sizeMed } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const base = rgb255(WATER.wetAsphaltDay);
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const u = x / size, v = y / size;
        const rip = tileFbm(this.noise, u, v, 5.0, 3);
        const fine = tileFbm(this.noiseB, u, v, 19.0, 2);
        const k = 1 + (rip - 0.5) * 0.22 + (fine - 0.5) * 0.10;
        const a = 0.55 + rip * 0.45;
        const i = (y * size + x) * 4;
        d[i] = Math.min(255, base.r * k);
        d[i + 1] = Math.min(255, base.g * k);
        d[i + 2] = Math.min(255, base.b * k);
        d[i + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    return this._register('wet', toTexture(c, { aniso: this.aniso, name: 'wet' }));
  }

  /** Generic scorch/impact decal — a soft irregular blob with alpha. */
  decalBlob({ size = T.tex.sizeSmall, base = FX.tyreMark } = {}) {
    const c = makeCanvas(size, size);
    const g = ctx2d(c);
    const img = g.createImageData(size, size);
    const d = img.data;
    const rgbBase = rgb255(base);
    const half = size / 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = (x + 0.5 - half) / half, dy = (y + 0.5 - half) / half;
        const r = Math.sqrt(dx * dx + dy * dy);
        const wob = (this.noise.fbm(x * 0.07, y * 0.07, 3) - 0.5) * 0.45;
        const a = Math.max(0, 1 - Math.pow(Math.min(1, r + wob), 1.7));
        const i = (y * size + x) * 4;
        d[i] = rgbBase.r; d[i + 1] = rgbBase.g; d[i + 2] = rgbBase.b;
        d[i + 3] = Math.round(a * 255);
      }
    }
    g.putImageData(img, 0, 0);
    const tex = toTexture(c, { aniso: this.aniso, name: 'decal' });
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    return this._register('decal', tex);
  }
}

export default TextureFactory;
